import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
const slides = [{
  src: 'https://cdn.magicpatterns.com/uploads/oFcZ6EwDSGZ4QEUbXNBSnS/Screenshot_20260513-221911~2.png',
  caption: 'Módulo 3 · Estructura de Precios y Paquetes'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rDFZZ2wsaDNgx4jF4XZXGt/Screenshot_20260513-222005~2.png',
  caption: 'Cómo calcular tu precio real · Calculadora de gabinete'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9eYquRUfDaEyEcWGtGyo7H/Screenshot_20260510-161749~2.png',
  caption: 'Bloque 3 · Planilla de Zonas Trabajadas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2cPi3AWvfavQ4Rsx9Zr79R/Screenshot_20260510-161640~2.png',
  caption: 'Bloque 2 · Registro de Sesión'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/c9TtX43k2VXURxYLnLrdFv/Screenshot_20260510-161724~2.png',
  caption: 'Bloque 3 · Evolución General del paciente'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 4500);
    return () => clearInterval(id);
  }, [next, paused]);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 60 : -60,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -60 : 60,
      opacity: 0
    })
  };
  return <Section className="max-w-5xl" data-id="element-191">
      <div className="text-center mb-8 md:mb-12" data-id="element-192">
        <div className="inline-flex items-center justify-center bg-white border border-brand-rose/30 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-193">
          <span className="font-serif font-semibold tracking-wider text-brand-rose text-xs md:text-sm" data-id="element-194">
            VISTA PREVIA DEL KIT
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-serif font-bold leading-tight" data-id="element-195">
          📸 Mira el material por dentro
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-196">
          Páginas reales del kit: planillas, módulos y herramientas listas para
          usar en tu consultorio.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-197">
        <div className="relative glass-panel rounded-3xl overflow-hidden border border-brand-pink/30 bg-white/70 shadow-[0_10px_40px_rgba(212,115,140,0.12)]" data-id="element-198">
          <div className="relative w-full" style={{
          aspectRatio: '4 / 5'
        }} data-id="element-199">
            <AnimatePresence custom={direction} mode="wait" data-id="element-200">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 260,
                damping: 30
              },
              opacity: {
                duration: 0.25
              }
            }} className="absolute inset-0 flex items-center justify-center p-4 md:p-8" data-id="element-201">
                <img src={slides[index].src} alt={slides[index].caption} className="max-w-full max-h-full object-contain rounded-xl shadow-md" draggable={false} data-id="element-202" />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 border border-brand-pink/30 shadow-md flex items-center justify-center text-brand-rose hover:bg-white hover:scale-105 transition-all" data-id="element-203">
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-204" />
          </button>
          <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 border border-brand-pink/30 shadow-md flex items-center justify-center text-brand-rose hover:bg-white hover:scale-105 transition-all" data-id="element-205">
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-206" />
          </button>

          {/* Caption */}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-white/95 via-white/80 to-transparent px-4 md:px-8 pt-10 pb-4 md:pb-6 pointer-events-none" data-id="element-207">
            <p className="text-center text-xs md:text-sm font-medium text-brand-text/80" data-id="element-208">
              {slides[index].caption}
            </p>
          </div>
        </div>

        {/* Dots + counter */}
        <div className="flex items-center justify-center gap-2 mt-5" data-id="element-209">
          {slides.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-brand-rose' : 'w-2 bg-brand-pink/40 hover:bg-brand-pink/70'}`} data-id="element-210" />)}
        </div>
        <p className="text-center text-xs text-brand-muted mt-3" data-id="element-211">
          {index + 1} / {slides.length}
        </p>

        {/* Thumbnails */}
        <div className="hidden md:grid grid-cols-5 gap-3 mt-6" data-id="element-212">
          {slides.map((s, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Vista previa ${i + 1}`} className={`relative aspect-[4/5] rounded-xl overflow-hidden border-2 transition-all bg-white/70 ${i === index ? 'border-brand-rose shadow-md scale-[1.02]' : 'border-brand-pink/20 hover:border-brand-rose/50 opacity-70 hover:opacity-100'}`} data-id="element-213">
              <img src={s.src} alt={s.caption} className="w-full h-full object-cover" draggable={false} data-id="element-214" />
            </button>)}
        </div>
      </div>
    </Section>;
};